import {useCallback, useEffect, useRef, useState} from 'react';
import {COMMS_CHANGED, useCommsClient} from '../communications/communicationsClient';
import {applyAcknowledgements, combineNotifications} from './notificationModel';

const empty = token => ({token, messages: null, markets: null, errors: {}, loading: true, busy: false, operationError: null});
export function useNotificationFeed() {
  const {token, request} = useCommsClient();
  const [state, setState] = useState(() => empty(token));
  const mounted = useRef(true);
  const generation = useRef(0), currentScope = useRef(token), pending = useRef(null);
  currentScope.current = token;
  const visible = state.token === token ? state : empty(token);
  const snapshot = useRef(visible);
  snapshot.current = visible;

  const reload = useCallback(async () => {
    if (!mounted.current || !token || pending.current === token) return;
    const id = ++generation.current;
    // Keep the last confirmed snapshot during refresh. Clearing it flashes a false zero.
    setState(prev => ({...(prev.token === token ? prev : empty(token)), loading: true}));
    const results = await Promise.allSettled([
      request('/notifications?limit=100'),
      request('/markets/runs/notifications'),
    ]);
    if (!mounted.current || currentScope.current !== token || id !== generation.current) return;
    setState(prev => {
      const next = {...(prev.token === token ? prev : empty(token)), errors: {}, loading: false};
      ['messages', 'markets'].forEach((channel, index) => {
        const result = results[index];
        if (result.status === 'fulfilled') next[channel] = result.value;
        else if (channel === 'markets' && result.reason.status === 403) next[channel] = {notifications: [], unread_count: 0};
        else {
          if (result.reason.status === 401 || result.reason.status === 403) next[channel] = null;
          next.errors[channel] = result.reason.message || 'Notifications could not be checked.';
        }
      });
      return next;
    });
  }, [token, request]);

  useEffect(() => {
    mounted.current = true;
    reload();
    const timer = setInterval(reload, 30000);
    const focus = () => reload();
    window.addEventListener(COMMS_CHANGED, reload);
    window.addEventListener('notifications-updated', reload);
    window.addEventListener('focus', focus);
    return () => {mounted.current = false; generation.current++; clearInterval(timer); window.removeEventListener(COMMS_CHANGED, reload); window.removeEventListener('notifications-updated', reload); window.removeEventListener('focus', focus);};
  }, [reload]);

  const mark = useCallback(async (items = null) => {
    if (!token || currentScope.current !== token || pending.current === token) return;
    const before = snapshot.current;
    if (before.token !== token) return;
    pending.current = token;
    ++generation.current; // Invalidate GETs started before this mutation.
    setState(prev => ({...prev, busy: true, loading: false, operationError: null}));
    const selected = (items || combineNotifications(before.messages?.notifications, before.markets?.notifications)).filter(item => !item.read);
    const tasks = [];
    // "All" is available only after both sources loaded successfully. Its ordinary
    // endpoint includes older notices beyond the visible page.
    if (!items) tasks.push({allMessages: true, run: () => request('/notifications/mark-all-read', {method: 'POST'})});
    for (const item of selected) {
      if (!items && item.channel !== 'markets') continue;
      tasks.push({item, run: () => item.channel === 'markets'
        ? request(`/markets/runs/${encodeURIComponent(item.run_id)}/notification/read`, {method: 'POST', body: {notification_id: item.notification_id}})
        : request(`/notifications/${encodeURIComponent(item.notification_id)}/read`, {method: 'POST'})});
    }
    const results = await Promise.allSettled(tasks.map(async task => {
      const result = await task.run();
      if (result?.success === false) throw new Error('The notification could not be marked read.');
      return task;
    }));
    if (!mounted.current || currentScope.current !== token) {if (pending.current === token) pending.current = null; return;}
    const successes = results.filter(result => result.status === 'fulfilled').map(result => result.value);
    const failed = results.some(result => result.status === 'rejected');
    setState(prev => ({...applyAcknowledgements(prev, successes.filter(task => task.item).map(task => task.item), successes.some(task => task.allMessages)),
      busy: false, operationError: failed ? 'Some notifications could not be marked read. Successful updates were saved; try the remaining items again.' : null}));
    pending.current = null;
    // Reconcile successes even if another write failed; never leave a stale count.
    await reload();
  }, [token, request, reload]);

  const notifications = combineNotifications(visible.messages?.notifications, visible.markets?.notifications);
  const unread = (visible.messages?.unread_count || 0) + (visible.markets?.unread_count || 0);
  const marketUnreadsLoaded = (visible.markets?.notifications || []).filter(item => !item.read).length;
  const complete = !!visible.messages && !!visible.markets && !Object.keys(visible.errors).length;
  return {...visible, token, notifications, unread, complete, reload, mark,
    canMarkAll: complete && marketUnreadsLoaded >= (visible.markets.unread_count || 0),
    hasSnapshot: !!visible.messages || !!visible.markets};
}
